import GuiContext from "config/context/guiContext";
import { Fragment, useContext } from "react";
import Loader from "components/shared/loader/Loader";
import { useDatabaseAccess } from "../services/useDatabaseAccess";
import styles from "./DatabaseAccess.module.css";

/**
 * Ko dopire do izabrane baze — ljudskim imenima, iznad stabla ovlašćenja.
 *
 * Isto pitanje kao i stablo ispod, na drugoj visini: stablo kaže koji KLJUČEVI
 * stoje na kom ovlašćenju, ovo kaže ČIJI su. Zato stoje na istom ekranu — dva
 * ekrana za jedno pitanje bila bi ista greška koju smo ispravili na formi za
 * registraciju, samo krupnija.
 *
 * SPISAK NIKAD NIJE PRAZAN. Kad nikom ništa nije preneseno, vidi se sam vlasnik
 * sa svojim matičnim nalogom — spisak od jednog, ne prazan spisak. To je ono
 * što ovaj pregled čini samostalnim ekranom umesto nuspojave nekog dijaloga.
 */
const opisOvlascenja = (permissions) => {
    if (permissions.includes("owner")) return "owner";
    if (permissions.includes("active")) return "access";
    return "no key";
};

const DatabaseAccess = () => {
    const { selected: { selectedAccount } } = useContext(GuiContext);
    const { access, loading, error } = useDatabaseAccess(selectedAccount);

    if (!selectedAccount) return null;

    return (
        <section className={styles.panel}>
            <h3 className={styles.heading}>Who reaches {selectedAccount}</h3>

            {loading && <Loader />}

            {/* Greška NE prelazi u prazan spisak: „ne znam ko" i „nema nikoga"
                vode u suprotne zaključke, a drugi od njih poziva na brisanje. */}
            {!loading && error && <p className={styles.problem}>{error}</p>}

            {!loading && !error && access && (
                <>
                    {/* Prava tabela, ne spisak.
                        Prvo je bio `<ul>` u kome je SVAKI RED imao svoj grid, pa
                        se kolone među redovima nisu poravnavale. A bez zaglavlja
                        se `qatest1  you  qatest1  owner` čita kao ponovljena reč
                        — GUI ime i ime naloga se često zovu isto, i tek ih naziv
                        kolone razdvaja. Tabela poravnanje i imena nosi sama. */}
                    <div className={styles.tablewrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th scope="col">GUI user</th>
                                    {/* „Blockchain account" se citalo kao BAZA o kojoj je
                                        izvestaj, a to je nalog KOJI TAJ KORISNIK DRZI i
                                        cijim kljucem dopire dovde. Zaglavlje mora da
                                        kaze cije je, inace se dva naloga u istom redu
                                        stope u jedan. */}
                                    <th scope="col">Their account</th>
                                    <th scope="col">Access to {selectedAccount}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {access.people.map((p) => {
                                    const bezKljuca = p.permissions.length === 0;
                                    return (
                                        <Fragment key={p.guiname}>
                                            <tr className={bezKljuca ? styles.mismatch : undefined}>
                                                <td>
                                                    <span className={styles.guiname}>{p.guiname}</span>
                                                    {p.is_you && <span className={styles.you}>you</span>}
                                                </td>
                                                <td className={styles.account}>{p.account}</td>
                                                <td>
                                                    <span className={`${styles.pill} ${styles[`pill_${opisOvlascenja(p.permissions).replace(" ", "_")}`] || ""}`}>
                                                        {opisOvlascenja(p.permissions)}
                                                    </span>
                                                </td>
                                            </tr>
                                            {/* Stavka bez ključa obećava pristup kojeg nema. Red se
                                                ne izostavlja — baš taj nesklad je ono zbog čega
                                                korisnik i gleda ovaj ekran. */}
                                            {bezKljuca && (
                                                <tr className={styles.mismatch}>
                                                    <td colSpan={3} className={styles.rownote}>
                                                        {p.guiname} keeps {access.account} on their list,
                                                        but holds no key to it.
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {access.unattributed > 0 && (
                        <p className={styles.note}>
                            {access.unattributed === 1
                                ? "1 key belongs to no user of this GUI."
                                : `${access.unattributed} keys belong to no user of this GUI.`}{" "}
                            Someone may hold it outside this GUI, or have taken the
                            database off their list and kept the key.
                        </p>
                    )}

                    {!access.scan_complete && (
                        <p className={styles.problem}>
                            This list may be incomplete — the user table could not be
                            read to the end.
                        </p>
                    )}
                </>
            )}
        </section>
    );
};

export default DatabaseAccess;
